/**
 * AccessibilitySettings Component
 * 
 * Provides user controls for:
 * - Theme selection (System/Light/Dark)
 * - Reduce Motion preference
 * - High Contrast mode
 * - Font size adjustment (slider)
 */
import React, { useState, useEffect } from 'react';
import { 
  Sun, 
  Moon, 
  Eye, 
  Zap, 
  ZapOff, 
  Type,
  Contrast,
  Monitor,
  Laptop
} from 'lucide-react';
import {
  getReduceMotion,
  setReduceMotion,
  getHighContrast,
  setHighContrast,
  getFontSize,
  setFontSize,
  announce
} from '@/utils/accessibility';
import { getSavedTheme, applyTheme, THEMES, getSystemTheme, applySystemTheme } from '@/utils/themes';

// Theme mode options
const THEME_MODES = [
  { id: 'system', label: 'System', icon: Laptop },
  { id: 'light', label: 'Light', icon: Sun },
  { id: 'dark', label: 'Dark', icon: Moon },
];

export default function AccessibilitySettings() {
  const [themeMode, setThemeMode] = useState(() => {
    // Check if system mode was saved
    const savedMode = localStorage.getItem('guapgoals-theme-mode');
    return savedMode || 'system';
  });
  const [reduceMotion, setReduceMotionState] = useState(getReduceMotion());
  const [highContrast, setHighContrastState] = useState(getHighContrast());
  const [fontSize, setFontSizeState] = useState(getFontSize());

  // Font size options for the slider
  const fontSizeOptions = [
    { value: 85, label: 'S' },
    { value: 100, label: 'M' },
    { value: 115, label: 'L' },
    { value: 130, label: 'XL' },
  ];

  // Get the label for current font size
  const getFontSizeLabel = (value) => {
    const option = fontSizeOptions.find(opt => opt.value === value);
    return option ? option.label : 'M';
  };

  // Listen for system theme changes
  useEffect(() => {
    if (themeMode === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => {
        applySystemTheme();
      };
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [themeMode]);

  // Theme mode change handler
  const handleThemeModeChange = (newMode) => {
    setThemeMode(newMode);
    localStorage.setItem('guapgoals-theme-mode', newMode);
    
    if (newMode === 'system') {
      applySystemTheme();
      announce('Theme set to follow system preference');
    } else {
      applyTheme(newMode);
      announce(`Theme changed to ${newMode} mode`);
    }
  };

  // Reduce motion toggle
  const handleReduceMotionToggle = () => {
    const newValue = !reduceMotion;
    setReduceMotionState(newValue);
    setReduceMotion(newValue);
    announce(`Animations ${newValue ? 'disabled' : 'enabled'}`);
  };

  // High contrast toggle
  const handleHighContrastToggle = () => {
    const newValue = !highContrast;
    setHighContrastState(newValue);
    setHighContrast(newValue);
    announce(`High contrast mode ${newValue ? 'enabled' : 'disabled'}`);
  };

  // Font size slider change
  const handleFontSizeSlider = (e) => {
    const value = parseInt(e.target.value, 10);
    setFontSizeState(value);
    setFontSize(value);
    announce(`Font size set to ${getFontSizeLabel(value)}`);
  };

  return (
    <div className="space-y-4" role="region" aria-label="Accessibility Settings">
      {/* Section Header - Fixed contrast with explicit white/light text */}
      <div className="flex items-center gap-2 mb-4">
        <Eye 
          className="w-5 h-5 text-emerald-400" 
          aria-hidden="true" 
        />
        <h3 className="text-lg font-bold text-white">
          Accessibility
        </h3>
      </div>

      {/* Theme Selector - System/Light/Dark */}
      <div 
        className="p-4 rounded-xl"
        style={{ backgroundColor: 'var(--bg-surface)' }}
      >
        <div className="flex items-center gap-3 mb-3">
          <Sun className="w-5 h-5" style={{ color: 'var(--color-primary)' }} aria-hidden="true" />
          <div>
            <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
              Theme
            </p>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Choose your preferred appearance
            </p>
          </div>
        </div>
        
        {/* Segmented Button Row for Theme */}
        <div 
          className="flex rounded-lg p-1 mt-3"
          style={{ backgroundColor: 'var(--bg-surface-hover)' }}
          role="radiogroup"
          aria-label="Theme selection"
        >
          {THEME_MODES.map((mode) => {
            const Icon = mode.icon;
            const isSelected = themeMode === mode.id;
            return (
              <button
                key={mode.id}
                onClick={() => handleThemeModeChange(mode.id)}
                className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1`}
                style={{
                  backgroundColor: isSelected ? 'var(--color-primary)' : 'transparent',
                  color: isSelected ? 'var(--bg-primary)' : 'var(--text-primary)',
                }}
                role="radio"
                aria-checked={isSelected}
                data-testid={`theme-mode-${mode.id}`}
              >
                <Icon className="w-4 h-4" aria-hidden="true" />
                <span className="hidden sm:inline">{mode.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Reduce Motion Toggle */}
      <div 
        className="flex items-center justify-between p-4 rounded-xl"
        style={{ backgroundColor: 'var(--bg-surface)' }}
      >
        <div className="flex items-center gap-3">
          {reduceMotion ? (
            <ZapOff className="w-5 h-5" style={{ color: 'var(--color-primary)' }} aria-hidden="true" />
          ) : (
            <Zap className="w-5 h-5" style={{ color: 'var(--color-primary)' }} aria-hidden="true" />
          )}
          <div>
            <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
              Reduce Motion
            </p>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Minimizes animations for vestibular disorders
            </p>
          </div>
        </div>
        <button
          onClick={handleReduceMotionToggle}
          className="relative w-14 h-7 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          style={{ 
            backgroundColor: reduceMotion ? 'var(--color-primary)' : '#d1d5db'
          }}
          role="switch"
          aria-checked={reduceMotion}
          aria-label={`${reduceMotion ? 'Enable' : 'Disable'} animations`}
          data-testid="reduce-motion-toggle"
        >
          <span
            className="absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow-md transition-transform"
            style={{ transform: reduceMotion ? 'translateX(28px)' : 'translateX(0)' }}
            aria-hidden="true"
          />
        </button>
      </div>

      {/* High Contrast Toggle */}
      <div 
        className="flex items-center justify-between p-4 rounded-xl"
        style={{ backgroundColor: 'var(--bg-surface)' }}
      >
        <div className="flex items-center gap-3">
          <Contrast className="w-5 h-5" style={{ color: 'var(--color-primary)' }} aria-hidden="true" />
          <div>
            <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
              High Contrast
            </p>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Ultra-high contrast for low vision users
            </p>
          </div>
        </div>
        <button
          onClick={handleHighContrastToggle}
          className="relative w-14 h-7 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          style={{ 
            backgroundColor: highContrast ? 'var(--color-primary)' : '#d1d5db'
          }}
          role="switch"
          aria-checked={highContrast}
          aria-label={`${highContrast ? 'Disable' : 'Enable'} high contrast mode`}
          data-testid="high-contrast-toggle"
        >
          <span
            className="absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow-md transition-transform"
            style={{ transform: highContrast ? 'translateX(28px)' : 'translateX(0)' }}
            aria-hidden="true"
          />
        </button>
      </div>

      {/* Font Size Slider - Mobile-friendly */}
      <div 
        className="p-4 rounded-xl"
        style={{ backgroundColor: 'var(--bg-surface)' }}
      >
        <div className="flex items-center gap-3 mb-4">
          <Type className="w-5 h-5" style={{ color: 'var(--color-primary)' }} aria-hidden="true" />
          <div className="flex-1">
            <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
              Text Size
            </p>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Adjust text size for readability
            </p>
          </div>
          {/* Current size indicator */}
          <span 
            className="px-3 py-1 rounded-full text-sm font-bold"
            style={{ 
              backgroundColor: 'var(--color-primary)', 
              color: 'var(--bg-primary)' 
            }}
          >
            {getFontSizeLabel(fontSize)}
          </span>
        </div>
        
        {/* Slider Container */}
        <div className="px-1">
          {/* Labels */}
          <div className="flex justify-between text-xs mb-2" style={{ color: 'var(--text-secondary)' }}>
            <span>Small</span>
            <span>Extra Large</span>
          </div>
          
          {/* Custom Slider */}
          <div className="relative">
            <input
              type="range"
              min="85"
              max="130"
              step="15"
              value={fontSize}
              onChange={handleFontSizeSlider}
              className="w-full h-2 rounded-full appearance-none cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              style={{
                background: `linear-gradient(to right, var(--color-primary) 0%, var(--color-primary) ${((fontSize - 85) / 45) * 100}%, var(--bg-surface-hover) ${((fontSize - 85) / 45) * 100}%, var(--bg-surface-hover) 100%)`,
              }}
              aria-label="Text size slider"
              aria-valuemin={85}
              aria-valuemax={130}
              aria-valuenow={fontSize}
              aria-valuetext={`${getFontSizeLabel(fontSize)} (${fontSize}%)`}
              data-testid="font-size-slider"
            />
            
            {/* Tick marks */}
            <div className="flex justify-between mt-2">
              {fontSizeOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    setFontSizeState(option.value);
                    setFontSize(option.value);
                    announce(`Font size set to ${option.label}`);
                  }}
                  className={`w-8 h-8 rounded-full text-xs font-medium transition-all flex items-center justify-center focus:outline-none focus-visible:ring-2`}
                  style={{
                    backgroundColor: fontSize === option.value ? 'var(--color-primary)' : 'var(--bg-surface-hover)',
                    color: fontSize === option.value ? 'var(--bg-primary)' : 'var(--text-secondary)',
                    border: `2px solid ${fontSize === option.value ? 'var(--color-primary)' : 'transparent'}`
                  }}
                  aria-label={`Set text size to ${option.label}`}
                  data-testid={`font-size-${option.value}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Accessibility Info */}
      <div 
        className="p-4 rounded-xl border"
        style={{ 
          backgroundColor: 'var(--bg-surface)', 
          borderColor: 'var(--border-color)' 
        }}
      >
        <div className="flex items-start gap-3">
          <Monitor className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--text-secondary)' }} aria-hidden="true" />
          <div>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              <strong style={{ color: 'var(--text-primary)' }}>Keyboard Navigation:</strong> Use Tab to navigate, Enter/Space to activate, Escape to close dialogs.
            </p>
            <p className="text-sm mt-2" style={{ color: 'var(--text-secondary)' }}>
              <strong style={{ color: 'var(--text-primary)' }}>Screen Readers:</strong> This app supports VoiceOver, NVDA, and JAWS.
            </p>
          </div>
        </div>
      </div>

      {/* Custom slider styles */}
      <style>{`
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: var(--color-primary);
          cursor: pointer;
          border: 3px solid white;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
          transition: transform 0.15s ease;
        }
        input[type="range"]::-webkit-slider-thumb:hover {
          transform: scale(1.1);
        }
        input[type="range"]::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: var(--color-primary);
          cursor: pointer;
          border: 3px solid white;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
        }
        input[type="range"]:focus::-webkit-slider-thumb {
          box-shadow: 0 0 0 3px var(--color-primary), 0 0 0 6px rgba(16, 185, 129, 0.2);
        }
      `}</style>
    </div>
  );
}
